import { Badge, Button, cardClass, Table } from '@ledgerloom/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { propose } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Empty, ErrorBanner, ListSkeleton, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { day, money } from '../format';
import { useApi, usePageTitle } from '../hooks';
import { can, useSession } from '../session';
import { NotSetUp, periodName, useEntity } from './Books';

// Books (docs/plans/books-and-tickets-build-plan.md, B9): another system's trial balance
// beside the book, and the signed decision to leave that system behind. The server's
// engine says what ties and whether cutover may go; this only shows it. Patterns:
// data-display/table for the periods and for each period's differences, feedback/modal for the cutover.

interface Difference { account: string; name: string | null; imported_minor: number | null; book_minor: number | null; difference_minor: number }
interface RunPeriod { period_id: string; fiscal_year: number; number: number; ends_on: string; version: number; source_file: string; ties: boolean; accounts: number; differences: Difference[] }
interface Answer {
  entity_id: string; currency: string; needed: number; ready: boolean; reason: string | null; periods: RunPeriod[];
  cutover: { at: string; signed_by: string | null; reason: string; through: string } | null;
}

export function ParallelRun() {
  usePageTitle('Parallel run');
  const { me, refreshStats } = useSession();
  const { entity, picker, loading, error } = useEntity();
  const run = useApi<Answer>(entity ? `/v1/books/parallel-run?entity_id=${entity.id}` : null, [entity?.id]);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const data = run.data;

  const cutOver = async () => {
    setBusy(true);
    const why = `The ${data!.needed} newest periods tie to the old books`;
    try {
      const out = await propose('config.books_cutover', { entity_id: entity!.id, reason: why }, why);
      toast(outcomeMessage(out, 'Cut over'), outcomeTone(out));
      refreshStats();
      void run.reload();
    } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); setAsking(false); }
  };

  return (
    <div className="stack">
      <div className="page-head"><div><h1>Parallel run</h1><p>Your old system's trial balance beside these books, period by period, while you keep both. The imported balances are only for comparing: they never reach the journal or the statements.</p></div></div>
      {picker}
      <ErrorBanner error={error ?? run.error} />
      {loading || (entity && run.loading && !data) ? <section className={cardClass}><ListSkeleton rows={4} label="Loading the parallel run" /></section>
        : !entity ? <section className={cardClass}><NotSetUp /></section>
        : !data?.periods.length ? (
          <section className={cardClass}>
            <Empty title="No trial balance has come in yet">A feed brings one from your old system, as a file with each account's balance through the last day of a period. Add its connection in <Link to="/admin/connections">Connections</Link>, and a person signs each period before it lands.</Empty>
          </section>
        ) : (
          <>
            <section className={cardClass} aria-labelledby="cutover-h">
              <div className="card-head"><h2 id="cutover-h">Cutover</h2></div>
              {data.cutover ? (
                <div className="card-pad stack" style={{ gap: 8 }}>
                  <div><Badge tone="success">Cut over</Badge></div>
                  <p className="small" style={{ margin: 0 }}>{data.cutover.signed_by ?? 'A person'} signed this on {day(data.cutover.at)}, with {data.cutover.through} and the {data.needed - 1} periods before it tied. It records the decision and changes nothing else in Mazu.</p>
                </div>
              ) : data.ready ? (
                <div className="card-pad stack" style={{ gap: 8 }}>
                  <p className="small" style={{ margin: 0 }}>The {data.needed} newest periods tie to these books, one after another. You can leave the old system.</p>
                  {can(me, 'propose:config') && <div><Button onClick={() => setAsking(true)}>Cut over to these books</Button></div>}
                </div>
              ) : <p className="card-pad small" style={{ margin: 0 }}>{data.reason}</p>}
            </section>
            <section className={cardClass} aria-labelledby="periods-h">
              <div className="card-head"><h2 id="periods-h">Periods</h2><span className="faint small">In {data.currency}</span></div>
              <Table label="Imported periods">
                <caption className="sr-only">Imported periods</caption>
                <thead><tr><th scope="col">Period</th><th scope="col">Result</th><th scope="col">From</th></tr></thead>
                <tbody>{data.periods.map((p) => (
                  <tr key={p.period_id}>
                    <th scope="row" style={{ fontWeight: 'normal' }}>{periodName(p)}<div className="faint small">To {day(p.ends_on)}</div></th>
                    <td>{p.ties ? <Badge tone="success">Ties</Badge> : <Badge tone="danger">{p.differences.length === 1 ? 'One account differs' : `${p.differences.length} accounts differ`}</Badge>}</td>
                    <td>{p.source_file}{p.version > 1 && <div className="faint small">Corrected, version {p.version}</div>}</td>
                  </tr>
                ))}</tbody>
              </Table>
            </section>
            {data.periods.filter((p) => !p.ties).map((p) => (
              <section key={p.period_id} className={cardClass} aria-label={`What differs in ${periodName(p)}`}>
                <div className="card-head"><h2>What differs in {periodName(p)}</h2></div>
                <Table label={`Accounts that differ in ${periodName(p)}`}>
                  <caption className="sr-only">Accounts that differ in {periodName(p)}</caption>
                  <thead><tr><th scope="col">Account</th><th scope="col">Old system</th><th scope="col">These books</th><th scope="col">Difference</th></tr></thead>
                  <tbody>{p.differences.map((d) => (
                    <tr key={d.account}>
                      <th scope="row" style={{ fontWeight: 'normal' }}>{d.account}{d.name ? ` ${d.name}` : ''}</th>
                      <td>{d.imported_minor === null ? 'Not in the file' : side(d.imported_minor, data.currency)}</td>
                      <td>{d.book_minor === null ? 'No balance' : side(d.book_minor, data.currency)}</td>
                      <td>{money(Math.abs(d.difference_minor), data.currency)}</td>
                    </tr>
                  ))}</tbody>
                </Table>
              </section>
            ))}
          </>
        )}
      <ConfirmDialog open={asking} title="Cut over to these books?" confirmLabel="Send for approval" busy={busy} onCancel={() => setAsking(false)} onConfirm={() => { void cutOver(); }}>
        This records, once and for good, that these are now your books of record. Another person signs it. It changes nothing else in Mazu.
      </ConfirmDialog>
    </div>
  );
}

const side = (minor: number, currency: string) => `${minor < 0 ? 'Credit' : 'Debit'} ${money(Math.abs(minor), currency)}`;
